// frontend/src/components/common/DropdownMenu.jsx
import React, { useEffect, useRef, useCallback } from 'react';
import './DropdownMenu.css'; // You will create this CSS file for styling

/**
 * A reusable DropdownMenu component.
 * This is a controlled component, meaning its visibility (`isOpen`) is managed by its parent.
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Controls the visibility of the dropdown menu.
 * @param {function} props.onClose - Callback function to be called when the dropdown should be closed.
 * @param {React.RefObject} props.targetRef - A ref to the DOM element that triggers the dropdown. Used for positioning and click-outside logic.
 * @param {React.ReactNode} props.children - The menu items to be rendered inside the dropdown. Each child should ideally be a `<div>` wrapping a `<button>` or `<a>`.
 * @param {'bottom-start'|'bottom-end'|'top-start'|'top-end'|'right-start'|'right-end'|'left-start'|'left-end'} [props.placement='bottom-end'] - Preferred placement of the dropdown relative to the targetRef.
 * @param {string} [props.menuClassName=''] - Additional class name for the menu list `ul` element.
 */
const DropdownMenu = ({ 
  isOpen, 
  onClose, 
  targetRef, 
  children,
  placement = 'bottom-end', // Adjusted from common naming (e.g., Popper.js style)
  menuClassName = ''
}) => {
  const menuRef = useRef(null); // Ref for the actual dropdown menu container (the `ul`)

  // Close dropdown if clicking outside of the targetRef or the menu itself
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If menu is open and the click is outside both the trigger and the menu
      if (isOpen && 
          menuRef.current && !menuRef.current.contains(event.target) &&
          targetRef.current && !targetRef.current.contains(event.target)
      ) {
        onClose(); // Request parent to close the dropdown
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside); // For touch devices
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose, targetRef]);

  // Close dropdown on Escape key press
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose(); // Request parent to close
        targetRef.current?.focus(); // Return focus to the trigger button
      }
      // TODO: Implement keyboard navigation (ArrowUp, ArrowDown, Home, End, Enter, Space) for accessibility
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, targetRef]);

  // Only render the menu if it's open
  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={menuRef} // Attach ref to the actual menu element
      className={`dropdown-menu-container dropdown-placement-${placement} ${menuClassName}`}
      role="menu" // ARIA role for the list
      aria-orientation="vertical"
      // Consider adding aria-labelledby if the trigger element has an ID
    >
      {/* Children are expected to be the actual menu items */}
      {React.Children.map(children, child => {
          // Clone child to inject close logic if necessary, or ensure onClick handles it
          // For now, assuming children's onClick will handle closing the menu or that it's just a direct action.
          // Children should typically contain their own onClick handler which then calls onClose if needed.
          // A common pattern is to wrap buttons in <li> and give <li> role="menuitem"
          return child;
      })}
    </div>
  );
};

export default DropdownMenu;